import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authSlice';
import { useAccess } from '../access/AccessContext';

interface ProtectedRouteProps {
  children: ReactNode;
  permission?: string;
  groupId?: string;
  redirectTo?: string;
}

/**
 * Protected route component that checks authentication and optionally permission/group
 * 
 * Usage:
 * <Route path="/admin" element={
 *   <ProtectedRoute permission="admin:full">
 *     <AdminPage />
 *   </ProtectedRoute>
 * } />
 */
export function ProtectedRoute({ 
  children, 
  permission,
  groupId,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const { isAuthenticated, isInitializing } = useAuthStore();
  const { hasPermission, belongsToGroup } = useAccess();
  const location = useLocation();

  // Show loading state
  if (isInitializing) {
    return <div>Loading...</div>;
  }

  // Check authentication
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check permission if specified
  if (permission && !hasPermission(permission)) {
    return <Navigate to="/access-denied" replace />;
  }

  // Check group if specified
  if (groupId && !belongsToGroup(groupId)) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
}

/**
 * Admin-only route
 */
export function AdminRoute({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute permission="admin:full">
      {children}
    </ProtectedRoute>
  );
}
